import { useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';

import { PurchaseOrderDeliveryGetMinDto } from '~/src/types/PurchaseOrderDeliveryGetMinDto';
import { FormikToPurchaseOrderDeliveryToEdit } from '~/src/types/convertors/formikToPurchaseOrderDeliveryToEdit';
import { PurchaseOrderDeliveryFormik } from '~/src/types/PurchaseOrderDeliveryFormik';
import { createEmptyPurchaseOrderDeliveryFormik } from '~/src/types/convertors/createEmptyPurchaseOrderDeliveryFormik';
import { purchaseOrderDeliveryGetMinToFormik } from '~/src/types/convertors/purchaseOrderDeliveryGetMinToFormik';

import * as yup from 'yup';
import { AxiosResponse } from 'axios';

import WhitePad from '../../../../../components/common/WhitePad/WhitePad';
import PurchaseOrdersApi from '../../../../../services/api/PurchaseOrdersApi';
import FormRow from '../../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../../components/common/forms/TextInput/TextInputComponent';
import PrimaryButton from '../../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../../components/common/forms/Title/TitleComponent';
import PurchaseSecondaryNavigation from '~/src/components/common/navigation/PurchaseSecondaryNavigation';
import DatePickerInputComponent from '~/src/components/common/forms/DatePickerInput/DatePickerInputComponent';
import TimePickerInputComponent from '~/src/components/common/forms/TimePickerInput/TimePickerInputComponent';
import DeleteComponent from '~/src/components/common/DeleteComponent';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL } from '~/src/utils/constants';

export const deliverySchema = yup.object().shape({
  truckPlate: yup.string().required(),
  eta: yup.date(),
  driverFirstName: yup.string(),
  driverLastName: yup.string(),
  timeSlot: yup.date(),
  logisticsCompany: yup.string(),
  contacts: yup.string(),
  cmrDate: yup.date(),
  loadingReference: yup.string(),
  warehouseId: yup.string().required('Warehouse is required'),
});

interface ParamProps {
  id: string;
}

export default function DeliveryForm(): React.ReactElement {
  const { id } = useParams<ParamProps>();
  const history = useHistory();
  const location = useLocation();
  const delivery = location?.state?.delivery;

  const saveItem = async (
    values: PurchaseOrderDeliveryFormik,
    setSubmitting: (isSubmitting: boolean) => PurchaseOrderDeliveryGetMinDto,
  ): Promise<void> => {
    setSubmitting(true);
    let result: AxiosResponse<PurchaseOrderDeliveryGetMinDto> | null = null;
    values.orderId = delivery ? delivery.orderId : id;
    const convertedValues = FormikToPurchaseOrderDeliveryToEdit(values);
    if (delivery != undefined) {
      result = await PurchaseOrdersApi.updateDelivery(delivery.id, convertedValues);
    } else {
      result = await PurchaseOrdersApi.createDelivery(convertedValues);
    }
    if (result.status === StatusCodes.OK) {
      history.go(-1);
    } else {
      setSubmitting(false);
    }
  };

  const getInitialValues = ((): PurchaseOrderDeliveryFormik => {
    if (delivery) {
      const deliveryFormikInitial = purchaseOrderDeliveryGetMinToFormik(delivery);
      return deliveryFormikInitial;
    } else {
      const emptyDeliveryFormik = createEmptyPurchaseOrderDeliveryFormik();
      return emptyDeliveryFormik;
    }
  })();

  return (
    <>
      <WhitePad>
        <PurchaseSecondaryNavigation />

        <TitleComponent
          title={`Delivery ${delivery ? 'Edit ' : 'Create '} Order ${delivery ? '#' + delivery?.orderNumber : ''}`}
        />
        {getInitialValues && (
          <Formik
            initialValues={getInitialValues}
            validationSchema={deliverySchema}
            onSubmit={(values, { setSubmitting }) => {
              saveItem(values, setSubmitting);
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              errors,
              touched,
              initialValues,
            }) => (
              <form onSubmit={handleSubmit} autoComplete="off">
                <FormRow>
                  <TextInputComponent
                    name="driverFirstName"
                    label="Driver First Name"
                    value={values.driverFirstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                  <TextInputComponent
                    name="driverLastName"
                    label="Driver Last Name"
                    value={values.driverLastName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                  <TextInputComponent
                    name="truckPlate"
                    label="Truck Plate"
                    value={values.truckPlate}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                  <DatePickerInputComponent
                    name="eta"
                    label="ETA"
                    value={values.eta}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    onChange={(e): void => {
                      setFieldValue('eta', e.value);
                    }}
                    defaultValue={initialValues.eta}
                    cols={3}
                  />
                  <TimePickerInputComponent
                    name="timeSlot"
                    label="Тime Slot"
                    value={values.timeSlot}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    onChange={(e): void => {
                      setFieldValue('timeSlot', e.value);
                    }}
                    defaultValue={initialValues.timeSlot}
                    cols={3}
                  />

                  <TextInputComponent
                    name="logisticsCompany"
                    label="Logistics Company"
                    value={values.logisticsCompany}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />

                  <TextInputComponent
                    name="contacts"
                    label="Contacts"
                    value={values.contacts}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                </FormRow>

                <FormRow>
                  <DatePickerInputComponent
                    name="cmrDate"
                    label="CMR Date"
                    value={values.cmrDate}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    onChange={(e): void => {
                      setFieldValue('cmrDate', e.value);
                    }}
                    defaultValue={initialValues.cmrDate}
                    cols={3}
                  />
                  <TextInputComponent
                    name="cmrNumber"
                    label="CMR Number"
                    value={values.cmrNumber}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                  <TextInputComponent
                    name="loadingReference"
                    label="loadingReference"
                    value={values.loadingReference}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    errors={errors}
                  />
                  <AutocompleteComponent
                    url={`${API_URL}/warehouses/get`}
                    name="warehouseId"
                    label="WarehouseId"
                    initialValue={values.warehouseId}
                    onChange={setFieldValue}
                    queryFields={['name']}
                    getOptionLabel={(option) => `${option.name}`}
                    errorMessage={errors.warehouseId}
                    cols={3}
                  />
                </FormRow>
                <div className="d-flex justify-content-between">
                  <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Submit" />
                </div>
                {delivery && <DeleteComponent id={delivery.id} onDelete={PurchaseOrdersApi.deleteDelivery} />}
              </form>
            )}
          </Formik>
        )}
      </WhitePad>
    </>
  );
}
